/**
 * Adresář ACM uzlů — čistá logika panelu „Uzly ACM" (filtr, seed widgetu pro
 * platform admina). Data jdou výhradně přes MCP `acm-self.node_directory`.
 */
import type { IdentityMe } from './identity-me.ts';

export const NODE_DIRECTORY_WIDGET_ID = 'acm-self.node-directory';

/** Řádek z `acm-self.node_directory` (viz modules/acm-self/node-directory.ts). */
export interface NodeDirectoryRow {
  id: string | null;
  node_id: string | null;
  label: string;
  environment: string | null;
  customer: string | null;
  dashboard_url: string | null;
  mcp_url: string | null;
  notes: string | null;
  source: 'mesh' | 'manual' | 'both';
  local: boolean;
  online: boolean | null;
  last_seen: string | null;
  role: string | null;
  discovered_dashboard_url: string | null;
}

export interface NodeDirectoryResult {
  ok: boolean;
  error?: string;
  rows?: NodeDirectoryRow[];
  counts?: { total: number; manual: number; mesh: number; both: number; online: number };
  discovery?: { at: string; cached: boolean; unreachable: string[] };
}

/** Platform admin = ten, komu tool `acm-self.node_directory` (acm:admin) odpoví. */
export function isPlatformAdmin(me: IdentityMe | undefined | null): boolean {
  const caps = me?.capabilities;
  return caps?.is_admin === true || caps?.platform === true;
}

function hostOf(url: string | null): string {
  if (!url) return '';
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

/** Fulltext přes popisek, zákazníka, prostředí, poznámku a hosty URL. */
export function filterDirectoryRows(rows: NodeDirectoryRow[], query: string): NodeDirectoryRow[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  const words = q.split(/\s+/);
  return rows.filter((r) => {
    const hay = [
      r.label,
      r.customer,
      r.environment,
      r.notes,
      r.role,
      hostOf(r.dashboard_url),
      hostOf(r.mcp_url),
      r.node_id,
    ].filter(Boolean).join(' ').toLowerCase();
    return words.every((w) => hay.includes(w));
  });
}

/** Stav uzlu pro StatusDot: online / offline / neznámý. */
export function onlineStatus(
  row: Pick<NodeDirectoryRow, 'online'>,
): 'online' | 'error' | 'unknown' {
  if (row.online === true) return 'online';
  if (row.online === false) return 'error';
  return 'unknown';
}

type Format = 'sm' | 'md' | 'lg';
interface SeedablePanel {
  id: string;
  widgets: Array<{ widgetId: string; format: Format }>;
}
export interface SeedableState {
  activeId: string;
  panels: SeedablePanel[];
  /** Widgety, které už byly jednou nabídnuté — po odebrání se nevrací. */
  seeded?: string[];
}

/**
 * Dashboard plochy si uživatel skládá sám a ukládají se (LS + ui.prefs), takže
 * přidání do výchozího layoutu by existující adminy minulo. Widget se proto
 * jednou vloží na začátek první plochy a zapíše do `seeded` — když ho admin
 * odebere, už se nevrátí. Beze změny vrací STEJNÝ objekt (žádný re-render cyklus).
 */
export function seedWidgetOnce<S extends SeedableState>(
  state: S,
  widgetId: string,
  format: Format,
): S {
  if (state.seeded?.includes(widgetId)) return state;
  const seeded = [...(state.seeded ?? []), widgetId];
  if (state.panels.some((p) => p.widgets.some((w) => w.widgetId === widgetId))) {
    return { ...state, seeded };
  }
  const target = state.panels[0];
  if (!target) return state;
  return {
    ...state,
    seeded,
    panels: state.panels.map((p) =>
      p.id === target.id ? { ...p, widgets: [{ widgetId, format }, ...p.widgets] } : p
    ),
  };
}
